/**
 * SilLopaWorksheet — B.6.2 per `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.1.
 *
 * SIL/LOPA table listing each Safety Instrumented Function with target SIL,
 * achieved SIL, PFDavg, voting, and LOPA initiating events. Persists at
 * pdo.safety.sil_lopa via PUT /projects/{id}/sil-worksheet.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, ShieldAlert } from "lucide-react";
import api, { formatErr } from "../lib/api";

const SIL_LEVELS = ["SIL1", "SIL2", "SIL3", "SIL4"];
const VOTINGS = ["1oo1", "1oo2", "2oo2", "2oo3", "2oo4"];

export default function SilLopaWorksheet({ projectId }) {
  const [busy, setBusy] = useState(false);
  const [sifs, setSifs] = useState([]);
  const [toast, setToast] = useState(null);

  const show = (t, kind = "ok") => {
    setToast({ t, kind });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const r = await api.get(`/projects/${projectId}/sil-worksheet`);
        setSifs(r.data?.sifs || []);
      } catch (e) {
        show(formatErr(e?.response?.data?.detail) || e.message, "err");
      } finally { setBusy(false); }
    })();
  }, [projectId]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${projectId}/sil-worksheet`, { sifs });
      show(`Saved — ${r.data.sif_count} SIFs`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const addSif = () => {
    setSifs([...sifs, {
      sif_id: `SIF-${(sifs.length + 1).toString().padStart(3, "0")}`,
      hazard: "", initiator: "", final_element: "",
      demand_rate: 0.1, sil_target: "SIL1", sil_achieved: "SIL1",
      pfd_avg: 0.05, votings: "1oo1", loapscenarios: "",
    }]);
  };
  const upd = (i, p) => setSifs(sifs.map((s, j) => i === j ? { ...s, ...p } : s));
  const del = (i) => setSifs(sifs.filter((_, j) => j !== i));

  return (
    <div data-testid="sil-worksheet"
         style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <ShieldAlert size={14} color="var(--pf-accent)" />
        <span className="pf-overline" style={{ margin: 0 }}>SIL / LOPA (B.6.2)</span>
        <span style={{ fontSize: 11, color: "var(--pf-text-dim)",
                        fontFamily: "'JetBrains Mono', monospace" }}>
          · {sifs.length} SIFs
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" data-testid="sil-add-btn" onClick={addSif}
                 style={{ ...btn, borderStyle: "dashed" }}>
          <Plus size={12}/> Add SIF
        </button>
        <button className="btn btn-primary" data-testid="sil-save-btn"
                 onClick={save} disabled={busy} style={btn}>
          {busy ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save
        </button>
      </div>

      <div style={{ overflow: "auto", border: "1px solid var(--pf-border)",
                     borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse",
                          fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
          <thead style={{ background: "var(--pf-surface-2)" }}>
            <tr>
              {["SIF ID", "Hazard", "Initiator", "Final Element",
                "Demand/yr", "SIL Target", "Voting", "PFDavg", "SIL Achieved",
                "LOPA Scenarios", ""].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sifs.map((s, i) => (
              <tr key={i} data-testid={`sil-row-${i}`}>
                <td style={td}><input style={cellInp}
                         data-testid={`sil-${i}-id`}
                         value={s.sif_id}
                         onChange={(e) => upd(i, { sif_id: e.target.value })} /></td>
                <td style={td}><textarea style={cellInp} rows={2}
                         data-testid={`sil-${i}-hazard`}
                         value={s.hazard}
                         onChange={(e) => upd(i, { hazard: e.target.value })} /></td>
                <td style={td}><input style={cellInp}
                         data-testid={`sil-${i}-init`}
                         value={s.initiator}
                         onChange={(e) => upd(i, { initiator: e.target.value })} /></td>
                <td style={td}><input style={cellInp}
                         data-testid={`sil-${i}-fe`}
                         value={s.final_element}
                         onChange={(e) => upd(i, { final_element: e.target.value })} /></td>
                <td style={td}><input style={cellInp} type="number" step="0.01"
                         data-testid={`sil-${i}-dr`}
                         value={s.demand_rate}
                         onChange={(e) => upd(i, { demand_rate: parseFloat(e.target.value) || 0 })} /></td>
                <td style={td}>
                  <select style={tdInp} value={s.sil_target}
                           data-testid={`sil-${i}-target`}
                           onChange={(e) => upd(i, { sil_target: e.target.value })}>
                    {SIL_LEVELS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <select style={tdInp} value={s.votings}
                           data-testid={`sil-${i}-vote`}
                           onChange={(e) => upd(i, { votings: e.target.value })}>
                    {VOTINGS.map((v) => <option key={v}>{v}</option>)}
                  </select>
                </td>
                <td style={td}><input style={cellInp} type="number" step="0.001"
                         data-testid={`sil-${i}-pfd`}
                         value={s.pfd_avg || 0}
                         onChange={(e) => upd(i, { pfd_avg: parseFloat(e.target.value) || 0 })} /></td>
                <td style={td}>
                  <select style={tdInp} value={s.sil_achieved || "SIL1"}
                           data-testid={`sil-${i}-ach`}
                           onChange={(e) => upd(i, { sil_achieved: e.target.value })}>
                    {SIL_LEVELS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </td>
                <td style={td}><textarea style={cellInp} rows={2}
                         data-testid={`sil-${i}-scen`}
                         value={s.loapscenarios}
                         onChange={(e) => upd(i, { loapscenarios: e.target.value })} /></td>
                <td style={td}>
                  <button className="btn btn-ghost"
                           data-testid={`sil-${i}-del`}
                           onClick={() => del(i)}
                           style={{ ...btn, color: "#ef4444", padding: "2px 4px" }}>
                    <Trash2 size={10}/>
                  </button>
                </td>
              </tr>
            ))}
            {!sifs.length && (
              <tr><td colSpan={11} style={{ ...td, textAlign: "center",
                                                color: "var(--pf-text-dim)" }}>
                No SIFs defined. Click "Add SIF".
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {toast && (
        <div data-testid={`sil-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 28, right: 28,
          background: toast.kind === "ok" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          padding: "8px 14px", fontSize: 12, borderRadius: 3, zIndex: 10001,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{toast.t}</div>
      )}
    </div>
  );
}

const btn = { fontSize: 11, padding: "4px 10px", borderRadius: 3 };
const inp = { background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
               color: "var(--pf-fg)", padding: "4px 8px", fontSize: 11,
               borderRadius: 3, fontFamily: "'JetBrains Mono', monospace" };
const th = { padding: "6px 8px", textAlign: "left", color: "var(--pf-text-dim)",
              borderBottom: "1px solid var(--pf-border)", fontSize: 10,
              whiteSpace: "nowrap", textTransform: "uppercase" };
const td = { padding: 4, borderBottom: "1px solid var(--pf-border-dim, #222)",
              verticalAlign: "top" };
const tdInp = { ...inp, width: "100%", fontSize: 10, padding: "2px 4px" };
const cellInp = { ...inp, width: "100%", minWidth: 100, fontSize: 10, padding: "2px 4px",
                   resize: "vertical" };
